/**
 * Device Quiz Component
 * Six questions; each answer maps to journey_go, journey_xtr, or xtr_pro.
 * Result: recommend by majority (ties favor higher-end: XTR Pro > JourneyXTR > JourneyGo).
 * On submit: hide quiz, show the matching featured-product section.
 */

/**
 * Returns the product handle with the highest vote count.
 * On tie, returns the first handle that reached the max count.
 */
function getRecommendedProductHandle(counts) {
  let maxCount = 0;
  let recommendedHandle = '';

  Object.keys(counts).forEach((handle) => {
    const count = counts[handle];
    if (count > maxCount) {
      maxCount = count;
      recommendedHandle = handle;
    }
  });

  return recommendedHandle;
}

const KLAVIYO_CLIENT_SUBSCRIPTIONS_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const KLAVIYO_API_REVISION = '2026-01-15';

/** Fallback auto-dismiss delay (ms) when the user doesn't click "Continue". */
const ERROR_AUTO_DISMISS_MS = 5000;

if (!customElements.get('device-quiz-container')) {
  customElements.define(
    'device-quiz-container',
    class DeviceQuizContainer extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.steps = Array.from(this.querySelectorAll('.quiz__step'));
        this.questionSteps = this.steps.filter((s) => s.hasAttribute('data-device-quiz-step'));
        this.formStep = this.querySelector('.quiz__step--form');
        this.currentStepIndex = 0;

        this.nextButton = this.querySelector('[data-device-quiz-next]');
        this.prevButton = this.querySelector('[data-device-quiz-prev]');
        this.quizForm = this.querySelector('[data-quiz-form]');
        this.emailInput = this.querySelector('[data-quiz-email]');
        this.formError = this.querySelector('[data-quiz-form-error]');
        this.klaviyoCompanyId = (
          this.getAttribute('data-klaviyo-company-id') || ''
        ).trim();
        this.klaviyoListId = (this.getAttribute('data-klaviyo-list-id') || '').trim();
        this.subscriptionFailureBehavior =
          this.getAttribute('data-quiz-on-failure') || 'block';

        this.messages = {
          invalidEmail: this.getAttribute('data-msg-invalid-email') || 'Please enter a valid email address',
          genericError: this.getAttribute('data-msg-generic-error') || 'An error occurred. Please try again.',
          networkError: this.getAttribute('data-msg-network-error') || 'Unable to submit your email right now. Please try again.',
          subscriptionFailed: this.getAttribute('data-msg-subscription-failed') || 'Subscription failed. Please try again.',
          continueButton: this.getAttribute('data-msg-continue-button') || 'Continue',
        };

        this.init();
      }

      init() {
        if (this.nextButton) {
          this.nextButton.addEventListener('click', this.handleNext.bind(this));
        }
        if (this.prevButton) {
          this.prevButton.addEventListener('click', this.handlePrev.bind(this));
        }
        if (this.quizForm) {
          this.quizForm.addEventListener('submit', this.handleSubmit.bind(this));
        }
        this.addEventListener('change', this.handleAnswerChange.bind(this));

        this.hideResultSections();
        this.updateNavigation();
      }

      hideResultSections() {
        // Keep result sections visible while editing in the theme customizer
        if (window.Shopify && window.Shopify.designMode) return;

        const sections = document.querySelectorAll(
          '.device-quiz-result-section, .quiz-result-step-section'
        );
        sections.forEach((el) => {
          el.classList.add('hidden');
          el.setAttribute('aria-hidden', 'true');
        });
      }

      showStep(index) {
        this.steps.forEach((step, i) => {
          if (i === index) {
            step.classList.remove('hidden');
            step.setAttribute('aria-hidden', 'false');
            const firstInput = step.querySelector('input, textarea, select');
            if (firstInput && !firstInput.disabled) {
              setTimeout(() => firstInput.focus(), 100);
            }
          } else {
            step.classList.add('hidden');
            step.setAttribute('aria-hidden', 'true');
          }
        });
      }

      validateCurrentStep() {
        const currentStep = this.steps[this.currentStepIndex];
        if (!currentStep || currentStep.classList.contains('quiz__step--form')) {
          return true;
        }
        const selected = currentStep.querySelector('input[type="radio"]:checked');
        if (!selected) {
          const radios = currentStep.querySelectorAll('input[type="radio"]');
          radios.forEach((r) => r.classList.add('invalid'));
          return false;
        }
        currentStep.querySelectorAll('.invalid').forEach((el) => el.classList.remove('invalid'));
        return true;
      }

      handleNext() {
        if (!this.validateCurrentStep()) return;

        if (
          this.formStep &&
          this.currentStepIndex === this.steps.indexOf(this.formStep) - 1 &&
          this.currentStepIndex === this.questionSteps.length - 1
        ) {
          this.currentStepIndex = this.steps.indexOf(this.formStep);
          this.showStep(this.currentStepIndex);
          this.updateNavigation();
        } else if (this.currentStepIndex < this.steps.length - 1) {
          this.currentStepIndex++;
          this.showStep(this.currentStepIndex);
          this.updateNavigation();
        }
      }

      handlePrev() {
        if (this.currentStepIndex > 0) {
          this.currentStepIndex--;
          this.showStep(this.currentStepIndex);
          this.updateNavigation();
        }
      }

      handleAnswerChange(event) {
        const input = event.target;
        if (input.hasAttribute('data-quiz-answer')) {
          input.classList.remove('invalid');
          const option = input.closest('.quiz__answer-option');
          if (option) option.classList.remove('invalid');
        }
      }

      updateNavigation() {
        const currentStep = this.steps[this.currentStepIndex];
        const isFirstStep = this.currentStepIndex === 0;
        const isFormStep = currentStep && currentStep.classList.contains('quiz__step--form');
        const resultVisible = this.querySelector('.device-quiz__result-visible');

        if (resultVisible) {
          if (this.prevButton) this.prevButton.hidden = true;
          if (this.nextButton) this.nextButton.hidden = true;
          return;
        }

        if (isFormStep) {
          if (this.prevButton) {
            this.prevButton.hidden = false;
            this.prevButton.disabled = false;
            this.prevButton.classList.remove('quiz__button--disabled');
          }
          if (this.nextButton) this.nextButton.hidden = true;
          return;
        }

        if (this.prevButton) {
          this.prevButton.hidden = false;
          if (isFirstStep) {
            this.prevButton.disabled = true;
            this.prevButton.classList.add('quiz__button--disabled');
          } else {
            this.prevButton.disabled = false;
            this.prevButton.classList.remove('quiz__button--disabled');
          }
        }
        if (this.nextButton) this.nextButton.hidden = false;
      }

      collectProductHandleCounts() {
        const counts = {};
        this.questionSteps.forEach((step) => {
          const selected = step.querySelector('input[type="radio"]:checked');
          if (selected) {
            const handle = selected.getAttribute('data-product-handle');
            if (handle) {
              counts[handle] = (counts[handle] || 0) + 1;
            }
          }
        });
        return counts;
      }

      async handleSubmit(event) {
        event.preventDefault();

        const email = this.emailInput ? this.emailInput.value.trim() : '';
        if (!email) {
          this.showFormError(this.messages.invalidEmail);
          return;
        }
        this.emailInput.value = email;
        if (!this.emailInput.checkValidity()) {
          this.showFormError(this.messages.invalidEmail);
          return;
        }

        const counts = this.collectProductHandleCounts();
        const recommendedHandle = getRecommendedProductHandle(counts);

        if (this.formError) this.formError.classList.add('hidden');

        this.setSubmitButtonLoading(true);
        try {
          const subscriptionResult = await this.subscribeEmailToKlaviyo(email);
          if (!subscriptionResult.success) {
            if (this.subscriptionFailureBehavior !== 'continue') {
              this.showFormError(subscriptionResult.message);
              return;
            }

            await this.showFormError(subscriptionResult.message, {
              showContinue: true,
            });
          }

          this.showResult(recommendedHandle);
        } finally {
          this.setSubmitButtonLoading(false);
        }
      }

      /**
       * Displays an error message inside the form error banner.
       * @param {string} message - Error text to show.
       * @param {object} [options] - Display options.
       * @param {boolean} [options.showContinue=false] - Append a dismiss button.
       * @returns {Promise<void>|undefined} Resolves when the user
       *   clicks "Continue" or after ERROR_AUTO_DISMISS_MS, whichever
       *   comes first. Returns undefined when showContinue is false.
       */
      showFormError(message, { showContinue = false } = {}) {
        if (!this.formError) return undefined;

        this.formError.textContent = '';
        this.formError.setAttribute('role', 'alert');

        const text = document.createElement('span');
        text.textContent = message;
        this.formError.appendChild(text);

        this.formError.classList.remove('hidden');

        if (!showContinue) return undefined;

        return new Promise((resolve) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'quiz__form-error-dismiss';
          btn.textContent = this.messages.continueButton;
          this.formError.appendChild(btn);

          const timer = setTimeout(resolve, ERROR_AUTO_DISMISS_MS);
          btn.addEventListener(
            'click',
            () => {
              clearTimeout(timer);
              resolve();
            },
            { once: true },
          );
        });
      }

      setSubmitButtonLoading(isLoading) {
        const submitButton = this.quizForm && this.quizForm.querySelector('button[type="submit"]');
        if (!submitButton) return;

        if (isLoading) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.setAttribute('aria-busy', 'true');
          submitButton.disabled = true;
          return;
        }

        submitButton.setAttribute('aria-disabled', 'false');
        submitButton.removeAttribute('aria-busy');
        submitButton.disabled = false;
      }

      async subscribeEmailToKlaviyo(email) {
        if (!this.klaviyoCompanyId || !this.klaviyoListId) {
          console.error(
            'Klaviyo company ID and list ID must be set in section settings.',
          );
          return {
            success: false,
            message: this.messages.genericError,
          };
        }

        const endpoint = `${KLAVIYO_CLIENT_SUBSCRIPTIONS_ENDPOINT}?company_id=${encodeURIComponent(this.klaviyoCompanyId)}`;
        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: this.klaviyoListId,
                },
              },
            },
          },
        };

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Revision: KLAVIYO_API_REVISION,
            },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            return { success: true };
          }

          const responseBody = await response.json().catch(() => null);
          return {
            success: false,
            message: this.getKlaviyoErrorMessage(responseBody),
          };
        } catch {
          return {
            success: false,
            message: this.messages.networkError,
          };
        }
      }

      getKlaviyoErrorMessage(responseBody) {
        if (!responseBody || !Array.isArray(responseBody.errors) || responseBody.errors.length === 0) {
          return this.messages.subscriptionFailed;
        }

        const firstError = responseBody.errors[0] || {};
        return (
          firstError.detail ||
          firstError.title ||
          this.messages.subscriptionFailed
        );
      }

      showResult(recommendedProductHandle) {
        this.classList.add('device-quiz__result-visible');

        const sectionEl = this.closest('[id^="shopify-section-"]');
        if (sectionEl) {
          sectionEl.classList.add('device-quiz-section--result-visible');
        }

        const target = document.querySelector(
          `.device-quiz-result-section[data-device-quiz-result="${recommendedProductHandle}"]`
        );
        if (target) {
          target.classList.remove('hidden');
          target.setAttribute('aria-hidden', 'false');
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // Reveal any dedicated quiz result step sections (e.g. promo code banner)
        document.querySelectorAll('.quiz-result-step-section').forEach((el) => {
          el.classList.remove('hidden');
          el.setAttribute('aria-hidden', 'false');
        });

        this.updateNavigation();
      }
    }
  );
}
